import { Link } from "react-router-dom";
import { UserPlus, Play, History, ShieldCheck, Clock } from "lucide-react";
import { useDemoState } from "../demo/DemoStateProvider";
import { DEMO_THERAPIST } from "../demo/mock-data";

/**
 * DemoDashboard (Scope A2) — the pre-populated workspace a prospect lands in after
 * the (faked) login. Roster comes from the in-memory demo store, so a client added
 * via the add-client flow appears here and a refresh resets everything.
 */
export function DemoDashboard() {
  const { clients, lastSessionFor } = useDemoState();

  return (
    <main className="mx-auto max-w-5xl px-4 py-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--color-gold-soft)]">
            {DEMO_THERAPIST.clinic}
          </p>
          <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">
            Welcome back, {DEMO_THERAPIST.name.split(",")[0]}
          </h1>
        </div>
        <div className="flex gap-2">
          <Link
            to="/add-client"
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-navy-mid)] px-4 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]"
          >
            <UserPlus className="h-4 w-4" /> Add client
          </Link>
          <Link
            to="/session"
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-4 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            <Play className="h-4 w-4" /> Start session
          </Link>
        </div>
      </header>

      <section className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
            Your clients ({clients.length})
          </h2>
          <div className="flex items-center gap-4">
            <Link
              to="/agreements"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]"
            >
              <ShieldCheck className="h-3 w-3" /> Agreements &amp; BAA
            </Link>
            <Link
              to="/history"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]"
            >
              <History className="h-3 w-3" /> Check-in history
            </Link>
          </div>
        </div>

        <ul className="grid gap-3 sm:grid-cols-2">
          {clients.map((c) => (
            <li key={c.id}>
            <Link
              to={`/client/${c.id}`}
              className="flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-4 py-3.5 transition-colors hover:border-[var(--color-border-gold)]"
            >
              <div className="min-w-0">
                <p className="truncate font-heading text-[16px] font-medium text-[var(--color-text-primary)]">
                  {c.displayName}
                </p>
                <p className="mt-0.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-ghost)]">
                  <span>{c.state}</span>
                  <span aria-hidden>·</span>
                  <span>{c.modality}</span>
                  <span aria-hidden>·</span>
                  {lastSessionFor(c.id) ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {lastSessionFor(c.id)}
                    </span>
                  ) : (
                    <span>No sessions yet</span>
                  )}
                </p>
              </div>
              <span
                className={
                  c.consentStatus === "signed"
                    ? "inline-flex items-center gap-1 rounded-full border border-[var(--color-border-gold)] px-2.5 py-1 font-mono text-[9px] uppercase tracking-wide text-[var(--color-gold-soft)]"
                    : "inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2.5 py-1 font-mono text-[9px] uppercase tracking-wide text-[var(--color-text-muted)]"
                }
              >
                <ShieldCheck className="h-3 w-3" />
                {c.consentStatus === "signed" ? "Consent signed" : "Consent pending"}
              </span>
            </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
